import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../apiConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTruck, faTruckMoving, faSearch, faPlus, faEdit, 
    faTrashAlt, faWeightHanging, faBoxOpen, faRulerHorizontal,
    faTimes, faSave, faIdCard
} from '@fortawesome/free-solid-svg-icons';

const AdminCars = () => {
    const { t } = useTranslation();
    const [cars, setCars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentCar, setCurrentCar] = useState(null);

    const placeholderImg = 'https://placehold.co/100x70?text=No+Photo';
    const errorImg = 'https://placehold.co/100x70?text=Error';

    const fetchCars = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE}/api/cars`, {
                headers: { 
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'ngrok-skip-browser-warning': 'true'
                }
            });
            const data = await res.json();
            setCars(Array.isArray(data) ? data : (data.data || []));
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCars(); }, []);

    const handleDelete = async (id) => {
        if (window.confirm(t('delete_car_confirm'))) {
            try {
                const res = await fetch(`${API_BASE}/api/cars/${id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                const result = await res.json();
                if (result.success) setCars(prev => prev.filter(car => car.id !== id));
            } catch (err) { console.error("Delete error:", err); }
        }
    };

    const handleEditClick = (car) => {
        setCurrentCar({ ...car });
        setIsEditModalOpen(true);
    };

    const handleUpdate = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/cars/${currentCar.id}`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(currentCar)
            });
            const data = await res.json();
            if (data.success) {
                setIsEditModalOpen(false);
                fetchCars();
            }
        } catch (err) { console.error("Update error:", err); }
    };

    const filteredCars = cars.filter(car => 
        car.plate_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        car.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        car.vehicle_type?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const stats = {
        total: cars.length,
        gazel: cars.filter(c => c.vehicle_type?.toUpperCase() === 'GAZEL').length,
        zil: cars.filter(c => c.vehicle_type?.toUpperCase() === 'ZIL').length
    };

    return (
        <div className="admin-cars-wrapper">
            <style>{modernCarStyles}</style>
            
            <div className="page-header">
                <div>
                    <h1 className="main-title">{t('cars_management_title')}</h1>
                    <p className="sub-title">{t('cars_management_subtitle')}</p>
                </div>
                <div className="header-actions">
                    <div className="search-container">
                        <FontAwesomeIcon icon={faSearch} className="search-icon" />
                        <input 
                            type="text" 
                            placeholder={t('search_placeholder')} 
                            className="modern-input" 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                        />
                    </div>
                    <button className="add-car-btn">
                        <FontAwesomeIcon icon={faPlus} /> {t('add_car')}
                    </button>
                </div>
            </div>

            <div className="stats-container">
                <StatCard icon={faTruck} label={t('total')} value={stats.total} color="#4318FF" bg="#E7E9FB" />
                <StatCard icon={faTruckMoving} label={t('gazels')} value={stats.gazel} color="#05CD99" bg="#E6FAF5" />
                <StatCard icon={faTruckMoving} label={t('zils')} value={stats.zil} color="#FFB547" bg="#FFF5E9" />
            </div>

            <div className="content-card">
                {loading ? (
                    <div className="loading-state">{t('loading')}...</div>
                ) : (
                    <div className="table-responsive">
                        <table className="modern-table">
                            <thead>
                                <tr>
                                    <th>{t('image')}</th>
                                    <th>{t('name_type')}</th>
                                    <th>{t('parameters')}</th>
                                    <th>{t('plate_number')}</th>
                                    <th>{t('driver')}</th>
                                    <th>{t('action')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCars.length > 0 ? filteredCars.map(car => (
                                    <tr key={car.id}>
                                        <td data-label={t('image')}>
                                            <div className="car-image-container">
                                            
<img 
    src={
        car.vehicle_image 
            ? `${API_BASE.replace(/\/$/, '')}/${car.vehicle_image.replace(/^\//, '')}` 
            : placeholderImg
    } 
    alt={car.model || "car"}
    loading="lazy"
    decoding="async"
    onError={(e) => { 
        e.target.onerror = null; 
        e.target.src = errorImg; 
    }}
/>
                                            </div>
                                        </td>
                                        <td data-label={t('name_type')}>
                                            <div className="car-main-info">
                                                <span className="car-name">{car.display_name || car.vehicle_type}</span>
                                                <span className="car-type-tag">{car.vehicle_type}</span>
                                            </div>
                                        </td>
                                        <td data-label={t('parameters')}>
                                            <div className="specs-grid">
                                                <span className="spec-item"><FontAwesomeIcon icon={faWeightHanging} /> {car.max_weight}{t('unit_ton')}</span>
                                                <span className="spec-item"><FontAwesomeIcon icon={faBoxOpen} /> {car.total_volume}{t('unit_m3')}</span>
                                                <span className="spec-item"><FontAwesomeIcon icon={faRulerHorizontal} /> {car.length_meters}{t('unit_m')}</span>
                                            </div>
                                        </td>
                                        <td data-label={t('plate_number')}>
                                            <div className="plate-container">
                                                <div className="arm-plate">
                                                    <span className="plate-text">{car.plate_number}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td data-label={t('driver')}>
                                            <div className="driver-info">
                                                <span className="driver-name">{car.driver_name || t('no_driver')}</span>
                                                <span className="driver-id"><FontAwesomeIcon icon={faIdCard} /> ID: {car.user_id || '---'}</span>
                                            </div>
                                        </td>
                                        <td data-label={t('action')}>
                                            <div className="action-btns">
                                                <button className="act-btn edit" onClick={() => handleEditClick(car)}>
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                <button className="act-btn delete" onClick={() => handleDelete(car.id)}>
                                                    <FontAwesomeIcon icon={faTrashAlt} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="6" className="no-data-td">
                                            {t('no_cars_found')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {isEditModalOpen && (
                <div className="modal-backdrop" onClick={() => setIsEditModalOpen(false)}>
                    <div className="modern-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><FontAwesomeIcon icon={faEdit} /> {t('edit_title')}</h3>
                            <button className="close-modal" onClick={() => setIsEditModalOpen(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="input-group">
                                <label>{t('label_display_name')}</label>
                                <input value={currentCar?.display_name || ''} onChange={(e) => setCurrentCar({...currentCar, display_name: e.target.value})} />
                            </div>
                            <div className="grid-2">
                                <div className="input-group">
                                    <label>{t('label_weight')} (t)</label>
                                    <input type="number" value={currentCar?.max_weight || ''} onChange={(e) => setCurrentCar({...currentCar, max_weight: e.target.value})} />
                                </div>
                                <div className="input-group">
                                    <label>{t('label_volume')} (m³)</label>
                                    <input type="number" value={currentCar?.total_volume || ''} onChange={(e) => setCurrentCar({...currentCar, total_volume: e.target.value})} />
                                </div>
                            </div>
                            <div className="input-group">
                                <label>{t('label_plate')}</label>
                                <input value={currentCar?.plate_number || ''} onChange={(e) => setCurrentCar({...currentCar, plate_number: e.target.value})} />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-save" onClick={handleUpdate}><FontAwesomeIcon icon={faSave} /> {t('save')}</button>
                            <button className="btn-cancel" onClick={() => setIsEditModalOpen(false)}>{t('cancel')}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const StatCard = ({ icon, label, value, color, bg }) => (
    <div className="stat-card">
        <div className="stat-icon-box" style={{ background: bg, color: color }}>
            <FontAwesomeIcon icon={icon} />
        </div>
        <div className="stat-details">
            <span className="stat-val">{value}</span>
            <span className="stat-lab">{label}</span>
        </div>
    </div>
);

const modernCarStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
/* Սա wrapper-ի սկզբնական մասում է */
.admin-cars-wrapper { 
    padding: 30px; /* Desktop-ի համար */
    background: #f4f7fe; 
    min-height: 100vh; 
    font-family: 'Plus Jakarta Sans', sans-serif; 
}

/* Ավելացրու կամ փոխիր սա media query-ի մեջ */
@media (max-width: 768px) {
    .admin-cars-wrapper { 
        padding: 10px 5px; /* Կողքերից ընդամենը 5px */
    }
}
    
    .page-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 30px; flex-wrap: wrap; gap: 20px; }
    .main-title { font-size: clamp(22px, 4vw, 28px); color: #1b2559; font-weight: 800; letter-spacing: -1px; margin: 0; }
    .sub-title { color: #a3aed0; font-weight: 500; margin-top: 5px; }
    
    .header-actions { display: flex; gap: 12px; flex-wrap: wrap; width: 100%; justify-content: space-between; }
    .search-container { position: relative; flex: 1; max-width: 350px; }
    .search-icon { position: absolute; left: 18px; top: 50%; transform: translateY(-50%); color: #4318ff; }
    .modern-input { 
        width: 100%; padding: 14px 15px 14px 48px; border-radius: 16px; 
        border: 1px solid #e0e5f2; background: white; font-weight: 500;
        transition: 0.3s; box-sizing: border-box;
    }
    .modern-input:focus { border-color: #4318ff; outline: none; box-shadow: 0 10px 20px rgba(67, 24, 255, 0.05); }
    
    .add-car-btn { 
        background: #4318ff; color: white; border: none; padding: 14px 24px; 
        border-radius: 16px; font-weight: 700; cursor: pointer; transition: 0.3s;
        box-shadow: 0 10px 20px rgba(67, 24, 255, 0.2);
    }
    .add-car-btn:hover { transform: translateY(-3px); background: #3311cc; }

    .stats-container { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 30px; }
    .stat-card { 
        background: white; padding: 20px; border-radius: 20px; 
        display: flex; align-items: center; gap: 15px; 
        box-shadow: 0 18px 40px rgba(112, 144, 176, 0.08);
    }
    .stat-icon-box { width: 52px; height: 52px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 20px; }
    .stat-val { font-size: 22px; font-weight: 800; color: #1b2559; }
    .stat-lab { color: #a3aed0; font-size: 13px; font-weight: 600; text-transform: uppercase; }

    .modern-table { width: 100%; border-collapse: separate; border-spacing: 0 12px; }
    .modern-table th { padding: 15px 20px; color: #a3aed0; font-size: 12px; text-transform: uppercase; font-weight: 700; text-align: left; }
    .modern-table tbody tr { background: white; transition: 0.3s; }
    .modern-table td { padding: 15px 20px; border: none; vertical-align: middle; }
    .modern-table td:first-child { border-radius: 20px 0 0 20px; }
    .modern-table td:last-child { border-radius: 0 20px 20px 0; }

    /* IMAGE STYLING - Ավելի մաքուր ու object-fit-ով */
    .car-image-container { 
        width: 90px; height: 65px; border-radius: 14px; overflow: hidden; 
        background: #f8f9fa; border: 1px solid #eef0f7;
        display: flex; align-items: center; justify-content: center;
    }
    .car-image-container img { 
        width: 100%; height: 100%; object-fit: cover; 
    }

    /* SPECS STYLING - Հեռավորությունը պահված */
    .specs-grid { display: flex; flex-direction: column; gap: 8px; }
    .spec-item { 
        font-size: 13px; color: #2b3674; font-weight: 600;
        display: flex; align-items: center; gap: 8px;
    }
    .spec-item svg { color: #4318ff; width: 14px; }

    .car-name { display: block; font-weight: 700; color: #1b2559; font-size: 16px; }
    .car-type-tag { 
        display: inline-block; padding: 4px 12px; background: #e7e9fb; 
        color: #4318ff; border-radius: 8px; font-size: 11px; font-weight: 700; margin-top: 5px;
    }

    .arm-plate { 
        border: 2px solid #2b3674; padding: 4px 12px; border-radius: 8px; 
        background: white; font-weight: 800; color: #2b3674; font-size: 15px;
        display: inline-flex; align-items: center; border-left-width: 10px;
        border-left-color: #0033ad;
    }

    .act-btn { 
        width: 40px; height: 40px; border-radius: 12px; border: none; 
        cursor: pointer; transition: 0.2s; font-size: 16px; margin: 0 5px;
    }
    .act-btn.edit { background: #F4F7FE; color: #4318ff; }
    .act-btn.delete { background: #ffeeef; color: #ee5d50; }
    .act-btn:hover { transform: translateY(-2px); box-shadow: 0 5px 10px rgba(0,0,0,0.1); }

    .no-data-td { text-align: center; padding: 40px !important; color: #a3aed0; font-weight: 600; }

    @media (max-width: 992px) {
        .modern-table thead { display: none; }
        .modern-table tbody { display: grid; grid-template-columns: 1fr; gap: 20px; }
        .modern-table tr { 
            display: flex; flex-direction: column; padding: 0 20px 20px 20px; 
            border-radius: 24px; position: relative;
        }
        .modern-table tr::before {
            content: ""; display: block; height: 6px; width: calc(100% + 40px);
            margin-left: -20px; margin-bottom: 15px;
            background: linear-gradient(90deg, #f59e0b, #d97706);
        }
        .modern-table td { 
            display: flex; justify-content: space-between; align-items: center; 
            padding: 12px 0 !important; border-bottom: 1px solid #f4f7fe !important;
        }
        .modern-table td::before { 
            content: attr(data-label); font-weight: 800; font-size: 11px; 
            color: #a3aed0; text-transform: uppercase;
        }
        .modern-table td:last-child { border: none !important; }
        .action-btns { width: 100%; display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .act-btn { width: 100%; margin: 0; }
        .specs-grid { align-items: flex-end; }
    }

    /* Modals */
    .modal-backdrop { 
        position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
        background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000;
    }
    .modern-modal { 
        background: white; width: 95%; max-width: 500px; border-radius: 30px; 
        padding: 30px; position: relative;
    }
    .modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 25px; }
    .input-group { margin-bottom: 20px; }
    .input-group label { display: block; margin-bottom: 8px; font-weight: 700; color: #1b2559; font-size: 14px; }
    .input-group input { 
        width: 100%; padding: 12px 16px; border-radius: 12px; border: 1px solid #e0e5f2; 
        font-family: inherit; font-weight: 500; box-sizing: border-box;
    }
    .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
    .modal-footer { display: flex; gap: 10px; margin-top: 20px; }
    .btn-save { flex: 1; background: #4318ff; color: white; border: none; padding: 12px; border-radius: 12px; font-weight: 700; cursor: pointer; }
    .btn-cancel { flex: 1; background: #f4f7fe; color: #1b2559; border: none; padding: 12px; border-radius: 12px; font-weight: 700; cursor: pointer; }
`;

export default AdminCars;